import Request from '@/api/request';
import {
  IStatementListItem,
  IStatementStatusInfo,
  CommonObject,
  IStatementShortInfo,
} from '@/utils/types';
import { IProxy, IStatement, ISubject } from '@/types/services';

type GetUserIsNew = {
  userIsNew: boolean;
};

export type GetOrganizationStatementsParams = {
  filter: {
    status: string[];
    number: string;
    date: string;
  };
  page: number;
  itemsPerPage: number;
};

export interface CheckMrpaFilePayload {
  statementId?: string;
  statementType?: string;
  /** ссылка(link) на файл в ФХ */
  xml: string;
  /** ссылка(link) на файл в ФХ */
  sig: string;
}

export interface CheckMrpaGuidPayload {
  statementId?: string;
  statementType?: string;
  guid: string;
  issuerInn: string;
}

interface CreateSurchargeResponse {
  id: string;
}

export interface CreateSurchargePayload {
  parentId: string;
}

const api = new Request('lk/statement/');

export function getStatementInfo(statementId: string): Promise<IStatement> {
  return api
    .get(`statementInfo/?statementId=${statementId}`)
    .then((res: any) => {
      return res;
    });
}

export function getStatementInfoToShow(
  statementId: string
): Promise<CommonObject> {
  return api
    .get(`statementInfoToShow/?statementId=${statementId}`)
    .then((res: CommonObject) => {
      return res;
    });
}

export function getStatementStatusInfoByID(
  statementId: string
): Promise<IStatementStatusInfo> {
  return api
    .get(`getStatementStatusInfoByID/?statementId=${statementId}`)
    .then((res: IStatementStatusInfo) => {
      return res;
    });
}

export function getUserStatements(
  pageNumber: number,
  pageSize: number,
  filters: any
): Promise<IStatementListItem> {
  let url;
  let urlBase = `getUserStatements?pageNumber=${pageNumber}&pageSize=${pageSize}`;
  let params = '';

  if (filters?.statementType && filters.statementType !== 'NOT_SELECT') {
    params = params + `&statementType=${filters.statementType}`;
  }

  if (filters?.extId) {
    params = params + `&extId=${filters.extId}`;
  }

  if (filters?.createDate) {
    params = params + `&createDate=${filters.createDate}`;
  }

  if (filters?.status.length > 0) {
    params = params + `&status=${filters.status}`;
  }

  url = urlBase + params;

  return api.get(url || '').then((res: any) => {
    return res;
  });
}

export function getUserStatementTypes(): Promise<string> {
  return api.get(`getUserStatementTypes`).then((res: any) => {
    return res;
  });
}

export function searchStatementByNumber(data: any): Promise<any> {
  return api.post(`searchStatementByNumber`, data).then((res: any) => {
    return res;
  });
}

export function deleteStatement(id: string) {
  return api.delete(`deleteStatement?statementId=${id}`);
}

export function getUserIsNew(): Promise<GetUserIsNew> {
  return api.get('/userIsNew').then((res: GetUserIsNew) => {
    return res;
  });
}

export function getUserStatementStatuses() {
  return api.get('getUserStatementStatuses').then((res: any) => res);
}

export function createCopy(statementId: string) {
  return api.post(`createCopy?statementId=${statementId}`);
}

export function repeatSendPackage(id: string) {
  return api.post(`repeatSendPackage?statementId=${id}`);
}

function parseDate(dt: string | undefined) {
  if (!dt) return undefined;
  return dt.split('.').reverse().join('-');
}

export async function getOrganizationStatements({
  filter,
  page,
  itemsPerPage,
}: GetOrganizationStatementsParams): Promise<{
  items: IStatementShortInfo[];
  count: number;
}> {
  const status =
    filter.status.length > 0
      ? filter.status.map((i) => `status=${i}`).join('&')
      : '';
  return api.get(`getUserOrgStatements?${status}`, {
    params: {
      pageNumber: page - 1,
      pageSize: itemsPerPage,
      createDate: parseDate(filter.date),
      extId: filter.number || undefined,
    },
  });
}

export function getUserOrgStatementStatuses() {
  return api.get('getUserOrgStatementStatuses').then((res: any) => res);
}

export function checkMrpaSubjectFile(
  payload: CheckMrpaFilePayload
): Promise<ISubject> {
  return api.post(`checkMrpSubjectFile`, payload);
}
export function checkMrpaSubjectFileMultiple(
  payload: CheckMrpaFilePayload
): Promise<ISubject[]> {
  return api.post(`checkMrpSubjectFileMultiple`, payload);
}

export function checkMrpaSubjectGuid(
  payload: CheckMrpaGuidPayload
): Promise<ISubject> {
  return api.post(`checkMrpSubjectGuid`, payload);
}

export function checkMrpaSubjectGuidMultiple(
  payload: CheckMrpaGuidPayload
): Promise<ISubject[]> {
  return api.post(`checkMrpSubjectGuidMultiple`, payload);
}

export function checkMrpaParticipantFile(
  payload: CheckMrpaFilePayload
): Promise<ISubject> {
  return api.post(`checkMrpParticipantFile`, payload);
}

export function checkMrpaParticipantGuid(
  payload: CheckMrpaGuidPayload
): Promise<ISubject> {
  return api.post(`checkMrpParticipantGuid`, payload);
}

export function checkMrpaProxyFile(
  payload: CheckMrpaFilePayload
): Promise<IProxy> {
  return api.post(`checkMrpFile`, payload);
}

export function checkMrpaProxyGUID(
  payload: CheckMrpaGuidPayload
): Promise<IProxy> {
  return api.post(`checkMrpGUID`, payload);
}

export async function createSurcharge(
  payload: CreateSurchargePayload
): Promise<CreateSurchargeResponse> {
  return api.post('createSurcharge', payload);
}
